import { ChevronRight, ExpandLess as IconExpandLess, ExpandMore as IconExpandMore } from "@mui/icons-material";
import { IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material";
import { useState } from "react";
import { generatePath, useNavigate } from "react-router";

import { ObservationGroup } from "~/modules/project/screens/ProjectReviewVisitV2Screen/utils/groupObservationsByType";

import { FinalReportPlannerVisitProjectFragment } from "../../../fragment.generated";
import { FinalReportObservationsTableItemFragment, PreciseLocationFormFragment } from "./fragment.generated";

type Props = {
    observationGroup: ObservationGroup<FinalReportObservationsTableItemFragment>;
    preciseLocationForm: PreciseLocationFormFragment;
    project: FinalReportPlannerVisitProjectFragment;
};

function mapPreciseLocationAnswers(
    preciseLocation: Record<string, string> | null | undefined,
    preciseLocationForm: PreciseLocationFormFragment,
): Record<string, string> {
    if (!preciseLocation) return {};

    return Object.fromEntries(
        Object.entries(preciseLocation).map(([key, value]) => {
            const question = preciseLocationForm.questions.find(q => q.key === key);
            if (!question) return [key, value];

            if (question.type.__typename !== "GqlSettingsFormTemplateQuestionTypeDropdown") {
                return [key, value];
            }

            const option = question.type.options?.find(o => o.key === value);
            return [key, option?.name ?? value];
        }),
    );
}

export default function FinalReportObservationTableItem({ observationGroup, preciseLocationForm, project }: Props) {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const navigate = useNavigate();

    const handleClick = (observation: FinalReportObservationsTableItemFragment) => {
        navigate({
            pathname: generatePath("/c/:clientSlug/p/:projectSlug/map-editor/:layerId/:featureId", {
                clientSlug: project.client.slug,
                projectSlug: project.slug,
                layerId: observation.feature.layerId,
                featureId: observation.feature.id,
            }),
        });
    };

    return (
        <>
            <TableRow>
                <TableCell colSpan={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography>
                            {observationGroup.name} ({observationGroup.observations.length})
                        </Typography>
                        <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                            {isCollapsed ? <IconExpandLess /> : <IconExpandMore />}
                        </IconButton>
                    </Stack>
                </TableCell>
            </TableRow>
            {isCollapsed &&
                observationGroup.observations.map(observation => {
                    const mappedLocation = mapPreciseLocationAnswers(observation.preciseLocation, preciseLocationForm);
                    return (
                        <TableRow
                            key={observation.id}
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleClick(observation)}
                        >
                            <TableCell>
                                <Typography>{observation.species?.name}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography>{mappedLocation.where ?? "-"}</Typography>
                            </TableCell>
                            <TableCell>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography>{observation.preciseLocation?.address ?? "-"}</Typography>
                                    <ChevronRight />
                                </Stack>
                            </TableCell>
                        </TableRow>
                    );
                })}
        </>
    );
}
